/**
 * 로케일 컨텍스트와 `useT()`.
 *
 * 로케일 결정 순서: 저장된 선택 → 브라우저 언어 → 한국어.
 * 한국어가 기본인 이유는 기존 사용자 전원이 한국어 화면을 쓰고 있어서다 —
 * 영어를 기본으로 두면 브라우저 언어가 영어인 기존 사용자의 화면이 하루아침에 바뀐다.
 */
import { createContext, useContext } from 'react';
import { MESSAGES, LOCALES, type Locale, type Messages } from './messages';

const STORAGE_KEY = 'monit.locale.v1';
const DEFAULT_LOCALE: Locale = 'ko';

export function detectLocale(
  stored: string | null,
  navigatorLanguages: readonly string[]
): Locale {
  if (stored && (LOCALES as string[]).includes(stored)) return stored as Locale;
  for (const lang of navigatorLanguages) {
    const base = lang.toLowerCase().split('-')[0];
    if ((LOCALES as string[]).includes(base)) return base as Locale;
  }
  return DEFAULT_LOCALE;
}

export interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Messages;
}

const I18nContext = createContext<I18nValue | null>(null);

export const I18N_STORAGE_KEY = STORAGE_KEY;
export const I18N_DEFAULT_LOCALE = DEFAULT_LOCALE;
export { I18nContext };

/**
 * Provider 밖에서도 죽지 않고 기본 로케일로 동작한다.
 * 화면 하나를 마이그레이션하는 동안 트리 전체를 감싸지 않아도 되게 하려는 것이다 —
 * 점진 이행 중에 앱이 통째로 흰 화면이 되는 것보다 낫다.
 */
export function useT(): I18nValue {
  const ctx = useContext(I18nContext);
  if (ctx) return ctx;
  return { locale: DEFAULT_LOCALE, setLocale: () => {}, t: MESSAGES[DEFAULT_LOCALE] };
}

export type { Locale };
